import React, { useCallback, useEffect, useState } from 'react';
import { push } from 'connected-react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router';
import { checkChangeResource, getPreventTextLiveBeforeRecordingsUpload } from './utils';
import ModalPreventForm from '../../modal-prevent/ModalPreventForm';
import { customModalClasses } from '../../modal-prevent/types';
import CloseBrowserTabDetector from '../../../services/common/closeBrowserTabDetector';
import { selectCameraRecording } from '../../../store/modules/studio/selectors';


type Props = {
  approveCb?: () => void;
  customModalStyles?: customModalClasses;
  conditionForModalOpening?: boolean;
  cbWhenOpen? : () => void;
};

const redirectTexts = getPreventTextLiveBeforeRecordingsUpload();

export const StudioPreventChangeRouteModal = ({
  cbWhenOpen, approveCb, customModalStyles, conditionForModalOpening,
}: Props) => {
  const dispatch = useDispatch();

  const [redirectOpen, setRedirectOpen] = useState(false);
  const [nextRoute, setNextRoute] = useState('');
  const { block } = useHistory();
  const { url } = useRouteMatch();
  const isRecording = useSelector(selectCameraRecording);

  const cancelRedirectHandler = useCallback(() => {
    setRedirectOpen((prev) => !prev);
    dispatch(push(nextRoute));
    if (approveCb) approveCb();
  }, [setRedirectOpen, nextRoute, dispatch, approveCb]);
  const approveRedirectHandler = useCallback(() => {
    setRedirectOpen((prev) => !prev);
  }, [setRedirectOpen]);

  useEffect(() => {
    const unregister = block((e) => {
      if (!redirectOpen && checkChangeResource(url, e.pathname) && conditionForModalOpening) {
        setNextRoute(e.pathname);
        setRedirectOpen(true);
        if (cbWhenOpen) {
          cbWhenOpen();
        }
        return false;
      }
    });

    return () => unregister();
  }, [setRedirectOpen, setNextRoute, block, url, redirectOpen, conditionForModalOpening, cbWhenOpen]);


  useEffect(() => {
    const closeBrowser = new CloseBrowserTabDetector({ message: `${redirectTexts.title[0]} ${redirectTexts.title[1]}` });
    if (conditionForModalOpening) {
      closeBrowser.init();
    }
    return () => closeBrowser.destroy();
  }, [conditionForModalOpening, isRecording]);

  useEffect(() => {
    const keyDownHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && redirectOpen) {
        approveRedirectHandler();
        e.preventDefault();
        e.stopPropagation();
      }
    };

    window.addEventListener('keydown', keyDownHandler);
    return () => {
      window.removeEventListener('keydown', keyDownHandler);
    };
  }, [approveRedirectHandler, redirectOpen]);


  return (
    <ModalPreventForm
      open={redirectOpen}
      cancelHandler={cancelRedirectHandler}
      approveHandler={approveRedirectHandler}
      onEscapeKeyDown={() => { setTimeout(() => { approveRedirectHandler(); }, 250); }}
      title={redirectTexts.title}
      message={redirectTexts.message}
      approveText={redirectTexts.approveText}
      cancelText={redirectTexts.cancelText}
      customClasses={customModalStyles}
    />
  );
};
